import { useContext, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Briefcase, ChevronRight, FolderOpen, FilePlus2, Inbox } from 'lucide-react'
import { NOT_ENTITLED, SessionExpired, get, write } from '../../components/cases/api'
import { date, words } from '../../components/cases/format'
import {
  Button,
  Chip,
  Empty,
  Field,
  Modal,
  Notice,
  Panel,
  Refusal,
  Spinner,
} from '../../components/cases/ui'
import Shell from '../../components/cases/Shell'
import { useResource } from '../../components/cases/useResource'
import { AuthContext } from '../../context/AuthContext'
import { useRoleLabels } from '../../components/cases/roleLabels'

// ======================================================================
// MY CASES
// ======================================================================
//
// SORTED AS THE BACKEND RETURNS IT. acl.list_user_cases decides the
// order; this component does not re-sort, and there is no sort control.
// The list is also NOT filtered here — handling_only and include_closed
// are query parameters answered in SQL, so toggling them asks the
// server a different question rather than hiding rows the client
// already holds. Anything filtered in the browser is a row that already
// left the database, which is the defect acl.py's docstring opens with.

// ----------------------------------------------------------------------
// WHO IS OFFERED THE CREATE ACTION (task 17)
// ----------------------------------------------------------------------
//
// er_cases/create.py's CREATE_ROLES, mirrored — manager or admin, and
// never an executive or a superadmin.
//
// EXECUTIVE WAS REMOVED BY TASK 27, on both sides. Opening a case names
// a subject and starts a formal procedure against a colleague; it is an
// act performed by whoever manages the team or administers HR, and an
// executive who wants one opened asks the person who will run it.
//
// THIS DECIDES WHETHER TO DRAW A BUTTON AND NOTHING ELSE. create_case()
// runs the real gate on the request, and it refuses a superadmin first
// and separately because ai_agent.rbac.user_has_any_role answers True
// for a superadmin against any role. If this mirror ever drifts, the
// cost is a button that 403s — never a case opened by somebody who may
// not open one.
//
// IT IS READ FROM THE SESSION, NOT FROM THE SERVER. GET
// /api/cases/auth/nav-entry now answers true for a handler OR a
// creator, and deliberately does not say which — see its docstring: a
// field naming the branch would tell every manager on the platform,
// every page load, whether they are currently handling a case. The role
// is already in the session this component is holding, so asking for it
// again would publish a fact to obtain something it already has.
//
// THE UNION OF role AND roles, like the backend's user_role_set() and
// like AuthContext itself: somebody with role='staff' and
// roles=['manager'] genuinely holds manager, and a check that missed
// that would hide the button from somebody the server would have
// allowed.
const CREATE_ROLES = ['manager', 'admin']

function mayOpenACase(role, roles) {
  const held = new Set([role, ...(Array.isArray(roles) ? roles : [roles])].filter(Boolean))
  if (held.has('superadmin')) return false
  return CREATE_ROLES.some((r) => held.has(r))
}

export default function MyCases() {
  const [handlingOnly, setHandlingOnly] = useState(false)
  const [includeClosed, setIncludeClosed] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  // Bumped after a successful creation so the list is re-read from the
  // server rather than having the new case spliced in here. The row this
  // screen shows carries clock flags computed against the org's holiday
  // calendar, and a client-assembled row would be a second version of
  // the same case with different arithmetic behind it.
  const [reload, setReload] = useState(0)
  const [creating, setCreating] = useState(false)
  const [opened, setOpened] = useState(null)

  const { role, roles } = useContext(AuthContext)
  const canCreate = useMemo(() => mayOpenACase(role, roles), [role, roles])
  // The tenant's own word for each role (task 27), resolved once for
  // the whole list rather than per row. See
  // components/cases/roleLabels.js.
  const roleLabels = useRoleLabels()


  useEffect(() => {
    let live = true
    setLoading(true)
    setError(null)
    const params = new URLSearchParams({
      handling_only: String(handlingOnly),
      include_closed: String(includeClosed),
    })
    get(`/me?${params}`)
      .then((res) => {
        if (!live) return
        setData(res === NOT_ENTITLED ? { counts: { open: 0, handling: 0 }, cases: [] } : res)
      })
      .catch((e) => {
        if (!live || e instanceof SessionExpired) return
        setError(e)
      })
      .finally(() => live && setLoading(false))
    return () => {
      live = false
    }
  }, [handlingOnly, includeClosed, reload])

  return (
    <Shell>
      {/* FULL WIDTH (task 26). Every other page in this application —
          Leave, Attendance, Tickets, Payroll, the admin dashboard — is a
          plain padded div with no max-width, so the centred column this
          module used made Cases the odd screen out rather than the
          careful one. Prose inside is measured instead; see .measure in
          cases.css. */}
      <div className="w-full px-5 py-8 sm:px-8">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-head text-[26px] font-bold tracking-tight">Your cases</h1>
            <p className="mt-1 text-[13px] text-muted">
              Everything you have been given access to, in whatever capacity.
            </p>
          </div>
          {/* The same action the empty state offers. It is here as well
              because a create button that exists only while the list is
              empty disappears the moment it is first used — the manager
              who opened one case would have no way to open a second. */}
          {canCreate && (
            <Button icon={FilePlus2} onClick={() => setCreating(true)}>
              Open a case
            </Button>
          )}
        </header>

        {opened && (
          <div className="anim-fade mb-4 rounded-md border border-accent-line bg-accent-dim px-3.5 py-2.5 text-[12.5px] leading-relaxed text-ink">
            Opened <span className="mono text-accent">{opened.reference}</span> —{' '}
            {opened.title}.
          </div>
        )}

        {data && <Counts counts={data.counts} />}

        <div className="mb-4 mt-5 flex flex-wrap items-center gap-2">
          <Toggle
            on={handlingOnly}
            onChange={setHandlingOnly}
            label="Only cases I am handling"
          />
          <Toggle
            on={includeClosed}
            onChange={setIncludeClosed}
            label="Include closed cases"
          />
        </div>

        <Refusal error={error} onDismiss={() => setError(null)} />

        {loading && !data ? (
          <Panel>
            <Spinner label="Loading your cases" />
          </Panel>
        ) : data?.cases?.length ? (
          <ul className="space-y-2.5">
            {data.cases.map((c) => (
              <CaseRow key={c.case_id} item={c} roleLabels={roleLabels} />
            ))}
          </ul>
        ) : (
          <Panel>
            <NothingHere
              handlingOnly={handlingOnly}
              includeClosed={includeClosed}
              hasAnyOpen={(data?.counts?.open ?? 0) > 0}
              canCreate={canCreate}
              onCreate={() => setCreating(true)}
            />
          </Panel>
        )}
      </div>

      {creating && (
        <OpenCaseDialog
          onClose={() => setCreating(false)}
          onOpened={(receipt) => {
            setOpened(receipt)
            setCreating(false)
            setReload((n) => n + 1)
          }}
        />
      )}
    </Shell>
  )
}

/** ======================================================================
 *  THE AMBER RULE ON THIS SCREEN
 *  ======================================================================
 *
 *  An amber left border on rows where the policy clock says the target
 *  is close or past — FROM THE DATA, never a client-side guess.
 *
 *  THIS IS A CHANGE FROM THE STANDALONE APP, AND IT IS THE BACKEND THAT
 *  CHANGED. That version carried a long comment explaining that the
 *  flags did not exist on this response and could not be obtained
 *  safely: CaseListItem had ten fields and neither `breached` nor
 *  `in_reminder_window` was among them, and fetching CaseDetail per row
 *  was worse than no colour at all, because require_case("view_case")
 *  refuses a witness and a companion and acl.require_capability writes a
 *  DURABLE access_denied row on every refusal — so a witness opening
 *  their own list would stamp "access denied" into the hash chain of
 *  every case they are a witness to, on every page load, for a border
 *  colour.
 *
 *  Task 7 added both fields to CaseListItem for exactly that reason.
 *  The schema's own docstring says so: they are there "SO THAT A CLIENT
 *  NEVER FETCHES THE CASE DETAIL PER ROW, AND THAT IS A SECURITY
 *  PROPERTY RATHER THAN A PERFORMANCE ONE". So the colour is now a
 *  field read, with no extra request and no denial.
 *
 *  GUESSING IS STILL NOT AN OPTION and never becomes one.
 *  `new Date() > target_close_at` is a different answer wearing the same
 *  colour: wrong across a weekend, wrong on a paused case whose clock is
 *  frozen at paused_at, and wrong by an entire reminder window whose
 *  length only the tenant's configuration knows. Nothing below computes
 *  a date. `breached` and `in_reminder_window` are read and rendered. */

function CaseRow({ item, roleLabels }) {
  // breached wins over in_reminder_window. They are mutually exclusive
  // server-side — timeline_position sets the window flag only when NOT
  // breached — but the order is written down rather than assumed,
  // because a client that quietly depended on that would be wrong the
  // day it stopped being true.
  const clock = item.breached
    ? 'clock-breach'
    : item.in_reminder_window
      ? 'clock-warn'
      : ''
  return (
    <li>
      <Link
        to={`/cases/${item.case_id}`}
        className={`panel anim-rise block px-5 py-4 transition hover:border-line-strong ${clock}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="mono text-accent">{item.reference}</span>
              <h3 className="font-head text-[15px] font-semibold leading-snug">
                {item.title}
              </h3>
            </div>

            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              <Chip>{item.case_type_label}</Chip>
              <Chip tone="blue">{words(item.current_stage)}</Chip>
              <Chip tone={item.status === 'closed' ? undefined : 'accent'}>
                {words(item.status)}
              </Chip>
              {/* THE FLAG IN WORDS AS WELL AS IN COLOUR. An amber border
                  is invisible to a screen reader and to anyone who does
                  not know the convention, and "this case is late" is not
                  something to encode in a hue on a disciplinary
                  caseload. */}
              {item.breached && <Chip tone="amber">Past target</Chip>}
              {!item.breached && item.in_reminder_window && (
                <Chip tone="amber">Target approaching</Chip>
              )}
            </div>

            <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11.5px] text-faint">
              <span>
                Opened <span className="text-muted">{date(item.opened_at)}</span>
              </span>
              {item.target_close_at && (
                <span>
                  Target{' '}
                  <span className={item.breached ? 'text-amber' : 'text-muted'}>
                    {date(item.target_close_at)}
                  </span>
                </span>
              )}
              {/* THE CALLER'S OWN ROLES, never a party list. */}
              <span>
                You are{' '}
                <span className="text-muted">
                  {roleLabels.labelList(item.roles)}
                </span>
              </span>
            </div>
          </div>

          <ChevronRight
            size={17}
            strokeWidth={1.8}
            className="mt-1 shrink-0 text-faint"
          />
        </div>
      </Link>
    </li>
  )
}

function Counts({ counts }) {
  return (
    <div className="grid grid-cols-2 gap-2.5 sm:max-w-sm">
      <Stat icon={FolderOpen} label="Open" value={counts.open} />
      <Stat icon={Briefcase} label="You are handling" value={counts.handling} />
    </div>
  )
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="panel flex items-center gap-3 px-4 py-3">
      <Icon size={17} strokeWidth={1.7} className="shrink-0 text-accent" />
      <div className="min-w-0">
        <div className="font-head text-[20px] font-bold leading-none">{value}</div>
        <div className="mt-1 text-[11.5px] text-faint">{label}</div>
      </div>
    </div>
  )
}

function Toggle({ on, onChange, label }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      aria-pressed={on}
      className={`chip cursor-pointer transition ${
        on ? 'chip-accent' : 'hover:border-line-strong'
      }`}
    >
      <span
        className={`inline-block h-1.5 w-1.5 rounded-full ${
          on ? 'bg-accent' : 'bg-faint'
        }`}
      />
      {label}
    </button>
  )
}

/** EMPTY STATES THAT READ AS INTENTIONAL.
 *
 *  Three of these matter:
 *
 *    A WITNESS with one involvement, who has toggled "only cases I am
 *    handling": they hold no handling role, so the honest answer is
 *    that the filter excluded their case — not that they have none.
 *
 *    A PERSON WITH NONE: no party row anywhere. That is a complete and
 *    unremarkable answer and should look like one. It must not read as
 *    an error, and it must not hint that something is being withheld,
 *    because in this system there is no difference between "you are on
 *    no cases" and "you are on cases you may not see" — there is no
 *    such thing as the second.
 *
 *    A MANAGER WITH NONE, which is new in task 17 and is the reason
 *    this screen was revisited. The sidebar entry now appears for
 *    anybody who may OPEN a case, so a manager who is on no case can
 *    reach this screen for the first time: they see the icon, complete
 *    step-up, and arrive at nothing. That is the ACL working exactly as
 *    designed — step-up proves identity, party rows grant access, and
 *    they hold none — but a screen that says nothing at all reads as a
 *    module that is broken or still loading.
 *
 *  ======================================================================
 *  "NO CASES", NEVER "NO CASES YOU CAN SEE"
 *  ======================================================================
 *  The wording below never suggests that something exists and is being
 *  withheld. "No cases you have access to" would invite exactly the
 *  question the 404 contract spends the whole module refusing to answer,
 *  and it would be a worse lie than it looks: this response is not
 *  filtered by permission at all. acl.list_user_cases() selects from
 *  er_case_parties by user id, so a case the caller is not party to is
 *  not a row that was hidden — it is a row that was never theirs. */
function NothingHere({ handlingOnly, includeClosed, hasAnyOpen, canCreate, onCreate }) {
  if (handlingOnly && hasAnyOpen) {
    return (
      <Empty icon={Inbox} title="No cases you are handling">
        You have access to cases, but not as an investigator, decision maker or
        case owner. Turn off “only cases I am handling” to see them.
      </Empty>
    )
  }

  return (
    <>
      {!includeClosed ? (
        <Empty icon={Inbox} title="You are not party to any open case">
          A case appears here as soon as somebody adds you to one. If a case you
          were part of has finished, turn on “include closed cases” to see it.
        </Empty>
      ) : (
        <Empty icon={Inbox} title="You are not party to any case">
          A case appears here as soon as somebody adds you to one. If you were
          expecting something, the person who contacted you about it can tell
          you where it is.
        </Empty>
      )}

      {/* THE ONE ACTION THIS PERSON CAN TAKE FROM HERE, and the reason
          it belongs on an empty screen: everything else on this module
          starts from a case, and somebody with no party row has none to
          start from. Waiting to be added to a case is not an action.

          It is offered on exactly the terms create_case() allows — see
          mayOpenACase() — so nobody is shown a button that is going to
          refuse them, and a person who cannot open a case is shown no
          button and told nothing about one existing. */}
      {canCreate && (
        <div className="-mt-4 flex flex-col items-center gap-2 pb-6 text-center">
          <Button variant="primary" icon={FilePlus2} onClick={onCreate}>
            Open a case
          </Button>
          <p className="max-w-md text-[11.5px] leading-relaxed text-faint">
            You can open one because of your role. It does not give you access
            to anybody else’s.
          </p>
        </div>
      )}
    </>
  )
}

/** OPENING A CASE.
 *
 *  ======================================================================
 *  TWO FIELDS, AND THE PARTIES COME AFTERWARDS
 *  ======================================================================
 *  POST /api/cases also accepts owner_id, subject_id and reporter_id, and
 *  this form deliberately sends none of them.
 *
 *  Omitting owner_id is what makes the person opening the case its
 *  case_owner. Naming somebody else means THE CREATOR HOLDS NOTHING —
 *  create.py's module docstring is explicit that their very next request
 *  for the case they just opened returns the standard 404 — and a
 *  three-uuid form on an empty screen is the wrong place to make that
 *  decision by accident. Everyone else is added from the case's own
 *  Parties panel, where the consequences of each role are on screen and
 *  where every grant is audited the same way.
 *
 *  ======================================================================
 *  THE CASE TYPE IS PICKED, NOT TYPED (task 18)
 *  ======================================================================
 *  It was free text until task 18, because case types are per-tenant
 *  rows in er_case_types and no endpoint published them — so this dialog
 *  asked a manager to TYPE a key and learn the legal values from a
 *  refusal. GET /api/cases/vocabularies now returns the calling
 *  organisation's active types, and the field is a picker.
 *
 *  THE STAGES ARE SHOWN WITH THE TYPE, and that is the reason the
 *  endpoint returns them. "Disciplinary" and "Formal grievance" are two
 *  words; the procedure each one starts is nine steps that this
 *  organisation wrote down, and somebody opening a case about a named
 *  colleague should see which of them they are starting before they
 *  press the button, not discover it afterwards.
 *
 *  THE LIST IS NOT CACHED ACROSS DIALOG OPENINGS. useResource fetches
 *  when this component mounts, which is when the dialog opens — a case
 *  type added by an administrator five minutes ago is in the list. */
function OpenCaseDialog({ onClose, onOpened }) {
  const [caseTypeKey, setCaseTypeKey] = useState('')
  const [title, setTitle] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const { data: vocab, loading: loadingVocab } = useResource('/vocabularies')
  const types = vocab?.case_type ?? []
  const chosen = types.find((t) => t.key === caseTypeKey) || null

  const ready = caseTypeKey.trim() && title.trim()

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      // Trimmed, not otherwise validated. The title bound
      // (create.MAX_TITLE_LENGTH) and the case-type vocabulary are the
      // server's to enforce, and a client-side copy of either would be a
      // second rule to keep in step with the first.
      const receipt = await write('', {
        body: { case_type_key: caseTypeKey.trim(), title: title.trim() },
      })
      onOpened(receipt)
    } catch (e) {
      if (e instanceof SessionExpired) return
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={busy ? () => {} : onClose}
      title="Open a case"
      subtitle="You become the case owner. Everybody else — the subject, the investigator, anyone else who needs it — is added afterwards, from the case itself."
    >
      <Refusal error={error} onDismiss={() => setError(null)} />

      <div className="space-y-4">
        <Field
          label="Case type"
          required
          hint={
            // No hint under the empty-configuration notice: it already
            // says what a case type is, and repeating "the procedures
            // your organisation has configured" under "your
            // organisation has configured none" reads as a contradiction.
            !loadingVocab && types.length === 0
              ? undefined
              : chosen?.stages?.length
                ? `Opens at “${chosen.stages[0]}”. The rest of the procedure follows below.`
                : 'The procedures your organisation has configured. Each one has its own stages, target dates and reminders.'
          }
        >
          {loadingVocab ? (
            <Spinner label="Loading your organisation’s case types" />
          ) : types.length ? (
            <select
              className="field"
              autoFocus
              value={caseTypeKey}
              onChange={(e) => setCaseTypeKey(e.target.value)}
            >
              <option value="">Choose a case type…</option>
              {types.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
          ) : (
            /* NO CASE TYPES CONFIGURED. Not an error and not a refusal —
               this person may open a case, their organisation has not
               said what kind. Said plainly, with the fix named and
               pointed at the person who can make it, rather than
               letting them fill in a title and meet a 400. */
            <Notice tone="amber" title="No case types are configured">
              A case type is the procedure a case follows — its stages,
              its target dates and who is chased when one slips. Your
              organisation has not set any up yet, so there is nothing to
              open a case on. Whoever administers Kwery for your
              organisation can add them.
            </Notice>
          )}
        </Field>

        {/* THE PROCEDURE, IN THIS ORGANISATION'S OWN WORDS. Shown only
            once a type is chosen, because before that it is a list of
            lists. The first stage is marked because it is the one the
            case opens at — create.py takes stages[0] and nothing here
            chooses it. */}
        {chosen?.stages?.length > 0 && (
          <div className="rounded-md border border-line bg-surface-2 px-3.5 py-3">
            <div className="eyebrow mb-2">
              What “{chosen.label}” involves — {chosen.stages.length} stages
            </div>
            <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5">
              {chosen.stages.map((s, i) => (
                <li key={s} className="flex items-center gap-1.5">
                  <Chip tone={i === 0 ? 'accent' : undefined}>{s}</Chip>
                  {i < chosen.stages.length - 1 && (
                    <ChevronRight size={12} strokeWidth={2} className="text-faint" />
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}
        {chosen && chosen.stages.length === 0 && (
          /* An active type whose stages array the backend could not
             parse. It is listed rather than hidden — see CaseTypeOption
             — and opening a case on it returns a 409 naming the defect,
             so the honest thing is to say so before the button is
             pressed rather than after. */
          <Notice tone="amber" title="This case type has no usable stages">
            No case can be opened on it until its stage list is fixed.
            Choose another type, or ask whoever administers Kwery for
            your organisation to correct this one.
          </Notice>
        )}

        <Field
          label="Title"
          required
          hint="What this case is called in every list, email and bundle it appears in. It is visible to everybody who is given access, including the person the case is about."
        >
          <input
            className="field"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
      </div>

      <div className="mt-4">
        <Notice tone="accent" icon={FilePlus2} title="What this does">
          It opens a case with you as its owner and gives it a reference. It
          gives you no access to anything else, and nobody else can see it until
          you add them.
        </Notice>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="primary" busy={busy} disabled={!ready} onClick={submit}>
          Open case
        </Button>
      </div>
    </Modal>
  )
}
